/**
 * **A regra das invocações, neutra entre os dois modos** (gate V28H.M).
 *
 * Duas cartas põem uma entidade no mundo: o **Drone de Ataque** e o **Leviatã
 * Aéreo**. Elas existem nos dois modos - o catálogo diz `hunt, pvp` para ambas -
 * e o que é da CARTA vive aqui, uma vez.
 *
 * ## Porque neutro, e não uma cópia de cada lado
 *
 * Porque a regra de sobrevivência, o relógio dos treze segundos e a proporção
 * da vida não são de uma arena: são da carta. Uma segunda cópia divergia na
 * primeira correcção, e `pvp/` não pode sequer importar `hunt/`.
 *
 * O que fica de cada lado é o que é da ARENA: unidades, quem é inimigo, e como
 * uma entidade se desenha.
 *
 * ## A regra de sobrevivência, decidida uma vez
 *
 * **Uma invocação morre com quem a invocou.** No tique em que o dono morre, se
 * desliga ou abandona, e no tique em que a partida acaba. É a regra mais
 * simples que é sempre verdadeira, e é a única que não deixa uma entidade órfã
 * numa instantânea - o que seria uma entidade que uma reconexão ressuscita e
 * ninguém controla.
 *
 * O Leviatã tem um caso a mais, e é **APROVADO**: uma vez que alguém entre, ele
 * é destruído **13 s** depois, aconteça o que acontecer - incluindo se o piloto
 * morrer lá dentro, e incluindo o tempo de espera que ele ainda tivesse.
 */

/** O que esta entidade é. Uma lista fechada: duas cartas, duas formas. */
export type CardSummonKind = 'drone' | 'leviathan';

/**
 * O que qualquer invocação tem, em qualquer modo.
 *
 * Cada arena estende isto com o que é dela - identidades de participante no
 * duelo, ids de entidade na caça - e a aritmética de relógio é partilhada.
 */
export interface CardSummonClock {
  /**
   * Milissegundos de vida por gastar.
   *
   * No Drone é o tempo dele. No Leviatã é o tempo que ele espera por um piloto:
   * quando alguém entra, é {@link boardedRemainingMs} que passa a mandar.
   */
  lifeMs: number;
  /**
   * Milissegundos desde a ENTRADA, a contar para trás. **APROVADO: 13 s.**
   *
   * `0` enquanto ninguém entrou.
   */
  boardedRemainingMs: number;
}

/** Quantas invocações uma arena aguenta ao mesmo tempo. Um tecto, não uma regra. */
export const CARD_MAX_SUMMONS = 12;

/**
 * A vida de um Drone, em basis points da do dono.
 *
 * Do DONO e nunca um número absoluto, pela mesma razão que o dano de uma carta
 * é uma proporção: um drone com vida fixa seria descartável no fim da
 * progressão e imortal no princípio dela.
 */
export const CARD_DRONE_HEALTH_BPS = 3_000;

/** O dano do kamikaze de um Drone, em bps do dano-base do dono. **Provisório.** */
export const CARD_DRONE_KAMIKAZE_BPS = 30_000;

/** O raio do kamikaze, em bps do raio do casco do dono. **Provisório.** */
export const CARD_DRONE_KAMIKAZE_REACH_BPS = 40_000;

/**
 * A que distância um casco entra num Leviatã, em bps do raio do casco DELE.
 *
 * "Entrada física da nave" é uma decisão aprovada, e isto é a metade que a
 * simulação consegue afirmar: encostar entra. As entradas laterais são desenho,
 * e vivem em cada cena.
 *
 * **12 000 -> 20 000** (decisão do dono, gate V28H.X): "aumente um pouco a caixa
 * de colisão que define se a nave está próxima o suficiente para aparecer o
 * botão de pilotar". Dois raios de casco em vez de um e um quinto - a estrutura
 * é grande, e ter de encostar ao pixel para o convite aparecer fazia da entrada
 * uma habilidade de precisão que ninguém pediu.
 *
 * O botão e a sala leem o MESMO número: um convite que aparecesse antes de a
 * sala aceitar seria um botão que não funciona.
 */
export const CARD_LEVIATHAN_ENTRY_BPS = 20_000;

/**
 * Quanto tempo um Leviatã espera por um piloto antes de se desfazer.
 *
 * **Provisório.** Os 13 s de pilotagem são aprovados; quanto ele espera não é, e
 * este número existe para ele não ficar na arena para sempre se ninguém entrar.
 */
export const CARD_LEVIATHAN_IDLE_MS = 20_000;

/**
 * Faz correr os relógios de uma invocação, e diz se ela ACABOU.
 *
 * Um passo, e a mesma função nos dois modos - 16 ms na caça, 50 ms no duelo.
 * Devolve `true` quando esta entidade deve sair da arena neste tique.
 */
export function ageCardSummon(clock: CardSummonClock, stepMs: number): boolean {
  if (clock.boardedRemainingMs > 0) {
    /*
     * **Embarcado: só este relógio manda.**
     *
     * O contrato é explícito - destruição automática treze segundos após a
     * entrada - e "aconteça o que acontecer" inclui o tempo que ele ainda tinha
     * por gastar antes de alguém entrar. Continuar a descontar `lifeMs` faria um
     * Leviatã embarcado no último segundo durar um segundo.
     */
    clock.boardedRemainingMs = Math.max(0, clock.boardedRemainingMs - stepMs);
    return clock.boardedRemainingMs === 0;
  }
  clock.lifeMs = Math.max(0, clock.lifeMs - stepMs);
  return clock.lifeMs === 0;
}
